import React, { useState } from 'react';

import Icon from '../../../components/AppIcon';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })?.format(amount || 0);

const STATUS_STYLES = {
  Draft: 'bg-slate-500/15 text-slate-600',
  Submitted: 'bg-blue-500/15 text-blue-600',
  Accepted: 'bg-emerald-500/15 text-emerald-600',
  Rejected: 'bg-red-500/15 text-red-600',
  'Converted to Project': 'bg-violet-500/15 text-violet-600',
};

const BidDetailView = ({ bid, onClose, onConvertToProject }) => {
  const [showConfirm, setShowConfirm] = useState(false);

  if (!bid) return null;

  const bidNumber = bid?.bidNumber || bid?.bid_number || `B-${bid?.id?.slice(0, 6)}`;
  const customerName = bid?.customerName || bid?.customer_name || 'Unknown Customer';
  const projectName = bid?.projectName || bid?.project_name || 'Unnamed Project';
  const totalAmount = bid?.totalAmount ?? bid?.total_amount ?? 0;
  const laborCost = bid?.laborCost ?? bid?.labor_cost ?? 0;
  const equipmentCost = bid?.equipmentCost ?? bid?.equipment_cost ?? 0;
  const materialCost = bid?.materialCost ?? bid?.material_cost ?? 0;
  const description = bid?.description || '';
  const notes = bid?.notes || '';
  const statusStyle = STATUS_STYLES?.[bid?.status] || 'bg-muted text-muted-foreground';
  const isAccepted = bid?.status === 'Accepted';

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 z-40 backdrop-blur-sm" onClick={onClose} />

      {/* Full-screen panel */}
      <div className="fixed inset-0 z-50 bg-background flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-4 border-b border-border bg-background/95 backdrop-blur flex-shrink-0">
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-muted flex items-center justify-center flex-shrink-0"
          >
            <Icon name="ArrowLeft" size={18} className="text-foreground" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono font-semibold text-muted-foreground">{bidNumber}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusStyle}`}>{bid?.status}</span>
            </div>
            <h2 className="text-base font-bold text-foreground truncate">{projectName}</h2>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {/* Customer */}
          <div className="flex items-center gap-3 bg-muted/40 rounded-2xl p-3">
            <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
              <Icon name="Building2" size={20} className="text-accent" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Customer</p>
              <p className="text-sm font-semibold text-foreground">{customerName}</p>
            </div>
          </div>

          {/* Total amount */}
          <div className="bg-card border border-border rounded-2xl p-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">Total Bid Amount</p>
            <p className="text-3xl font-bold text-foreground">{formatCurrency(totalAmount)}</p>
          </div>

          {/* Cost breakdown */}
          {(laborCost > 0 || equipmentCost > 0 || materialCost > 0) && (
            <div className="bg-card border border-border rounded-2xl p-4 space-y-3">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Cost Breakdown</h3>
              {laborCost > 0 && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-500" />
                    <span className="text-sm text-foreground">Labor</span>
                  </div>
                  <span className="text-sm font-semibold text-foreground">{formatCurrency(laborCost)}</span>
                </div>
              )}
              {equipmentCost > 0 && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-amber-500" />
                    <span className="text-sm text-foreground">Equipment</span>
                  </div>
                  <span className="text-sm font-semibold text-foreground">{formatCurrency(equipmentCost)}</span>
                </div>
              )}
              {materialCost > 0 && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                    <span className="text-sm text-foreground">Materials</span>
                  </div>
                  <span className="text-sm font-semibold text-foreground">{formatCurrency(materialCost)}</span>
                </div>
              )}
            </div>
          )}

          {/* Description */}
          {description && (
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Description</h3>
              <p className="text-sm text-foreground bg-muted/40 rounded-xl p-3">{description}</p>
            </div>
          )}

          {/* Notes */}
          {notes && (
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Notes</h3>
              <p className="text-sm text-foreground bg-muted/40 rounded-xl p-3">{notes}</p>
            </div>
          )}
        </div>

        {/* Bottom action */}
        <div className="px-4 py-4 border-t border-border flex-shrink-0">
          {isAccepted ? (
            showConfirm ? (
              <div className="space-y-3">
                <p className="text-sm text-center text-foreground font-medium">Convert this bid to a project?</p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowConfirm(false)}
                    className="flex-1 py-3.5 rounded-2xl bg-muted text-foreground text-sm font-semibold min-h-[44px]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => { onConvertToProject(bid); setShowConfirm(false); }}
                    className="flex-1 py-3.5 rounded-2xl bg-emerald-500 text-white text-sm font-semibold min-h-[44px] active:bg-emerald-600 transition-colors"
                  >
                    Confirm
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowConfirm(true)}
                className="w-full flex items-center justify-center gap-2 bg-emerald-500 text-white rounded-2xl py-4 text-sm font-bold min-h-[44px] active:bg-emerald-600 transition-colors"
              >
                <Icon name="ArrowRight" size={18} />
                Convert to Project
              </button>
            )
          ) : (
            <button
              onClick={onClose}
              className="w-full flex items-center justify-center gap-2 bg-muted text-foreground rounded-2xl py-4 text-sm font-semibold min-h-[44px]"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default BidDetailView;
